import { useApp } from "@/context/AppContext";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import {
    ShieldCheck, LockKey, UsersThree, HeartStraight,
    ArrowRight, CheckCircle, Sparkle, EnvelopeSimple, CurrencyEur,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";

// Warm three-generation family hero image
const heroFamily = "https://images.unsplash.com/photo-1609220136736-443140cffec6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=2400";
const grandparentImg = "https://images.unsplash.com/photo-1585813597616-63e881759e9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxncmFuZHBhcmVudCUyMGFuZCUyMGdyYW5kY2hpbGQlMjBzbWlsaW5nJTIwdHJ1c3R3b3J0aHl8ZW58MHx8fHwxNzgzODU0MzE0fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
    const { t, user, lang } = useApp();
    const nav = useNavigate();

    return (
        <AppShell variant="landing">
            {/* HERO */}
            <section className="relative overflow-hidden">
                {/* Family background image */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url(${heroFamily})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center 30%",
                    }}
                    aria-hidden="true"
                />
                {/* Warm readable overlay: sage-to-cream gradient */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(105deg, rgba(244,247,246,0.96) 0%, rgba(244,247,246,0.88) 42%, rgba(244,247,246,0.45) 68%, rgba(28,46,45,0.35) 100%)",
                    }}
                    aria-hidden="true"
                />
                <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="max-w-3xl"
                    >
                        <div className="inline-flex items-center gap-2 h-8 px-3 pill bg-white/90 border border-shield-line text-xs font-semibold uppercase tracking-[0.15em] text-shield-forest" data-testid="hero-eyebrow">
                            <Sparkle size={14} weight="fill" aria-hidden="true" />
                            {t("landing.eyebrow")}
                        </div>
                        <h1
                            className="mt-6 font-heading font-bold tracking-tight text-shield-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] whitespace-pre-line drop-shadow-[0_2px_20px_rgba(255,255,255,0.5)]"
                            data-testid="hero-title"
                        >
                            {t("landing.heroTitle")}
                        </h1>
                        <p className="mt-6 text-lg sm:text-xl text-shield-ink/85 leading-relaxed max-w-2xl font-medium">
                            {t("landing.heroSub")}
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                onClick={() => nav(user ? "/dashboard" : "/register")}
                                className="group inline-flex items-center gap-2 h-14 px-8 pill bg-shield-terracotta text-white text-lg font-semibold shadow-lift hover:bg-shield-terracottaDeep transition-colors active:scale-[0.98]"
                                data-testid="hero-cta-primary"
                            >
                                {t("landing.ctaPrimary")}
                                <ArrowRight size={20} weight="bold" className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                            </button>
                            <a
                                href="#how"
                                className="inline-flex items-center gap-2 h-14 px-6 pill text-shield-forest bg-white/80 hover:bg-white text-base font-semibold transition-colors border border-shield-line"
                                data-testid="hero-cta-secondary"
                            >
                                {t("landing.ctaSecondary")}
                            </a>
                        </div>

                        <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
                            {t("landing.trustBadges").map((b) => (
                                <div key={b} className="flex items-center gap-2 text-sm font-medium text-shield-ink" data-testid={`trust-badge-${b}`}>
                                    <CheckCircle size={18} weight="fill" className="text-shield-success" aria-hidden="true" />
                                    {b}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="relative py-24 lg:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="max-w-3xl">
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                            {t("nav.howItWorks")}
                        </div>
                        <h2 className="mt-3 font-heading font-semibold tracking-tight text-shield-ink text-2xl sm:text-3xl lg:text-4xl">
                            {t("landing.howTitle")}
                        </h2>
                        <p className="mt-4 text-lg text-shield-muted leading-relaxed">{t("landing.howSub")}</p>
                    </div>

                    <div className="mt-16 grid lg:grid-cols-3 gap-6">
                        {[
                            { icon: LockKey, key: "step1", tone: "bg-shield-forest text-white" },
                            { icon: UsersThree, key: "step2", tone: "bg-shield-terracotta text-white" },
                            { icon: HeartStraight, key: "step3", tone: "bg-shield-forest text-white" },
                        ].map(({ icon: Icon, key, tone }, i) => (
                            <motion.article
                                key={key}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="card-soft p-8 lg:p-10 hover:-translate-y-1 transition-transform duration-300"
                                data-testid={`how-${key}`}
                            >
                                <div className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl ${tone}`}>
                                    <Icon size={26} weight="duotone" aria-hidden="true" />
                                </div>
                                <div className="mt-6 text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted">
                                    {t(`landing.${key}.label`)}
                                </div>
                                <h3 className="mt-2 font-heading font-semibold text-2xl text-shield-ink">{t(`landing.${key}.title`)}</h3>
                                <p className="mt-4 text-base text-shield-muted leading-relaxed">{t(`landing.${key}.text`)}</p>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECURITY */}
            <section id="security" className="relative py-24 lg:py-32 bg-shield-sage/50">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                            {t("nav.security")}
                        </div>
                        <h2 className="mt-3 font-heading font-semibold tracking-tight text-shield-ink text-2xl sm:text-3xl lg:text-4xl">
                            {t("landing.securityTitle")}
                        </h2>
                        <p className="mt-5 text-lg text-shield-muted leading-relaxed">{t("landing.securitySub")}</p>

                        <ul className="mt-8 space-y-4">
                            {t("landing.securityPoints").map((p) => (
                                <li key={p} className="flex items-start gap-3">
                                    <ShieldCheck size={22} weight="duotone" className="text-shield-forest mt-0.5 flex-shrink-0" aria-hidden="true" />
                                    <span className="text-base text-shield-ink font-medium">{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="relative">
                        <img
                            src={grandparentImg}
                            alt="Intergenerational trust"
                            className="rounded-3xl w-full max-h-[520px] object-cover shadow-lift"
                        />
                        <div className="absolute -bottom-6 -left-6 card-soft p-5 max-w-[280px] hidden sm:block">
                            <div className="flex items-center gap-2 text-shield-success text-sm font-semibold">
                                <CheckCircle size={18} weight="fill" aria-hidden="true" /> AES-256-GCM
                            </div>
                            <p className="mt-1 text-sm text-shield-muted">
                                {lang === "de"
                                    ? "Verschlüsselt im Browser. Wir sehen nur Rauschen."
                                    : "Encrypted in browser. We only see noise."}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRICE — monthly */}
            <section id="price" className="relative py-24 lg:py-32">
                <div className="mx-auto max-w-6xl px-6 lg:px-10 grid lg:grid-cols-5 gap-10 items-center">
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                            {t("nav.price")}
                        </div>
                        <h2 className="mt-3 font-heading font-semibold tracking-tight text-shield-ink text-2xl sm:text-3xl lg:text-4xl">
                            {t("landing.priceTitle")}
                        </h2>
                        <p className="mt-4 text-lg text-shield-muted leading-relaxed">{t("landing.priceSub")}</p>

                        {/* Digistore24 requirement: prominent "digital product / instant access" badge */}
                        <div className="mt-6 inline-flex items-center gap-2 h-10 px-4 pill bg-shield-forest/10 border border-shield-forest/30 text-shield-forest font-semibold text-sm" data-testid="product-type-badge">
                            <Sparkle size={14} weight="fill" aria-hidden="true" />
                            {t("landing.productType")}
                        </div>

                        {/* Digistore24 requirement: clear list of what buyer gets */}
                        <div className="mt-8" data-testid="product-includes">
                            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted mb-3">
                                {t("landing.includesTitle")}
                            </div>
                            <ul className="grid sm:grid-cols-2 gap-3">
                                {t("landing.priceIncludes").map((p) => (
                                    <li key={p} className="flex items-start gap-2 text-shield-ink">
                                        <CheckCircle size={20} weight="fill" className="text-shield-success flex-shrink-0 mt-0.5" aria-hidden="true" />
                                        <span className="font-medium">{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <button
                            onClick={() => nav(user ? "/dashboard" : "/register")}
                            className="group mt-10 inline-flex items-center gap-2 h-14 px-8 pill bg-shield-forest text-white text-lg font-semibold hover:bg-shield-forestDeep transition-colors shadow-lift active:scale-[0.98]"
                            data-testid="price-cta"
                        >
                            {t("landing.ctaFinal")}
                            <ArrowRight size={20} weight="bold" className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                        </button>
                        <p className="mt-3 text-xs text-shield-muted" data-testid="delivery-hint">
                            {t("landing.deliveryHint")}
                        </p>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="card-soft p-10 text-center">
                            <div className="text-sm uppercase tracking-[0.15em] font-semibold text-shield-muted">
                                {t("activate.priceBoxTop")} / {lang === "de" ? "MONTHLY" : "MONATLICH"}
                            </div>
                            <div className="mt-3 flex items-baseline justify-center gap-1">
                                <span className="font-heading font-bold text-shield-forest text-7xl leading-none">1€</span>
                                <span className="text-shield-muted text-lg font-medium">{t("activate.perMonth")}</span>
                            </div>
                            <div className="mt-2 text-shield-muted">
                                {t("activate.priceBoxBottom")} / {lang === "de" ? "cancel anytime" : "jederzeit kündbar"}
                            </div>
                            <div className="mt-6 h-px bg-shield-line" />
                            <div className="mt-6 flex flex-col items-center gap-2">
                                <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-semibold text-shield-forest">
                                    <CurrencyEur size={12} weight="bold" aria-hidden="true" /> via Digistore24
                                </div>
                                <div className="text-xs text-shield-muted">{t("landing.productType")}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Global footer via AppShell */}
        </AppShell>
    );
}
